import { Injectable, signal, computed } from '@angular/core';
import RestService from '../../services/core-rest.service';
import Session from '../../utils/session.service';

@Injectable({ providedIn: 'root' })
export class TrainingProgressService {

  constructor(private restService: RestService, private session: Session) {}

  private _programVersionCode = signal<string>('');
  private _summary = signal<any>(null);
  private _modules = signal<any[]>([]);
  private _sectionViews: { [moduleId: string]: number[] } = {};
  private _moduleDetails: { [moduleId: string]: any } = {};

  readonly completedCount = computed(() => this._summary()?.completedModules || 0);
  readonly overallProgress = computed(() => {
    const total = this._summary()?.totalModules || 0;
    return total ? Math.round((this.completedCount() / total) * 100) : 0;
  });
  readonly averageScore = computed(() => {
    const avg = this._summary()?.averageBestScore || 0;
    return Math.round(avg * 100);
  });
  readonly certificateUnlocked = computed(() => !!this._summary()?.certificateCode);

  setProgramVersionCode(code: string | null | undefined): void {
    this._programVersionCode.set((code || '').trim());
  }

  private getDeviceLanguage(): string {
    return (this.session.getSession()?.user?.deviceLanguage || 'en_GB').trim() || 'en_GB';
  }

  private withTrainingParams(params?: any): any {
    const code = this._programVersionCode();
    const base = { ...(params || {}) };
    if (!base.language) base.language = this.getDeviceLanguage();
    if (!code || base.programVersionCode) return base;
    return { ...base, programVersionCode: code };
  }

  async loadSummary(params?: any): Promise<any> {
    this._summary.set(await this.restService.getTrainingLearnerSummary(this.withTrainingParams(params)));
    return this._summary();
  }

  async loadModules(params?: any): Promise<any[]> {
    const modules = await this.restService.getTrainingLearnerModules(this.withTrainingParams(params));
    this._modules.set(modules || []);
    return this._modules();
  }

  async loadModuleDetail(moduleId: string | number): Promise<any> {
    const detail = await this.restService.getTrainingModuleDetail(this.withTrainingParams({ moduleId }));
    this._moduleDetails[moduleId] = detail;
    return detail;
  }

  async loadSectionViews(moduleId: string | number): Promise<number[]> {
    const views = await this.restService.getTrainingSectionViews(this.withTrainingParams({ moduleId }));
    const uniqueIds = new Set<number>();
    (views || []).forEach(v => {
      const id = Number((v as any)?.sectionId);
      if (!Number.isNaN(id)) uniqueIds.add(id);
    });
    this._sectionViews[moduleId] = Array.from(uniqueIds);
    return this._sectionViews[moduleId];
  }

  async loadQuizAttempt(moduleId: string | number): Promise<any> {
    try {
      return await this.restService.getTrainingQuizAttempt(this.withTrainingParams({ moduleId }));
    } catch {
      return null;
    }
  }

  getLearnerName(): string {
    const user = this.session.getSession().user;
    const sessionName = user.firstname + " " + user.lastname;
    return sessionName || this._summary()?.learnerName || '';
  }

  getModuleStatus(moduleId: string | number): 'not-started' | 'in-progress' | 'passed' {
    const mod = this._modules().find(m => (m.module?.id || m.module?.moduleId || m.moduleId) == moduleId);
    const status = mod?.progress?.status;
    if (!status) return 'not-started';
    if (status === 'not_started') return 'not-started';
    if (status === 'passed') return 'passed';
    return 'in-progress';
  }

  getModuleScore(moduleId: string | number): number | null {
    const mod = this._modules().find(m => (m.module?.id || m.module?.moduleId || m.moduleId) == moduleId);
    return mod?.progress?.bestScore ?? null;
  }

  getViewedCount(moduleId: string | number): number {
    const ids = this._sectionViews[moduleId] || [];
    const unique = Array.from(new Set(ids.filter((n: any) => typeof n === 'number' && !Number.isNaN(n))));
    const totalSections = this.getSectionCount(moduleId) || Number.MAX_SAFE_INTEGER;
    return Math.min(unique.length, totalSections);
  }

  hasViewedSection(moduleId: string | number, sectionId: string | number): boolean {
    const views = this._sectionViews[moduleId] || [];
    return views.includes(Number(sectionId));
  }

  getSectionCount(moduleId: string | number): number {
    return this._moduleDetails[moduleId]?.sections?.length || 0;
  }

  allSectionsViewed(moduleId: string | number): boolean {
    const count = this.getSectionCount(moduleId);
    return count > 0 && this.getViewedCount(moduleId) >= count;
  }

  async markSectionViewed(moduleId: string | number, sectionId: string | number): Promise<void> {
    await this.restService.markTrainingSectionViewed(this.withTrainingParams({ moduleId, sectionId }), {});
    if (!this._sectionViews[moduleId]) {
      this._sectionViews[moduleId] = [];
    }
    if (!this._sectionViews[moduleId].includes(Number(sectionId))) {
      this._sectionViews[moduleId].push(Number(sectionId));
    }
  }

  async submitQuiz(moduleId: string | number, answers: any, startedAt?: Date, durationSeconds?: number): Promise<any> {
    const payload: any = { answers, startedAt, durationSeconds };
    const attempt = await this.restService.submitTrainingQuiz(this.withTrainingParams({ moduleId }), payload);
    await this.loadSummary();
    await this.loadModules();
    return attempt;
  }
}
